import { useEffect } from 'react';
import PropTypes from 'prop-types';

// Extend the Window interface to include Clarity
declare global {
    interface Window {
        clarity: (action: string, ...args: unknown[]) => void;
    }
}

interface ClarityProps {
    projectId?: string;
    enabled?: boolean;
}

/**
 * Microsoft Clarity Analytics Component
 * Provides React integration for Microsoft Clarity tracking
 */
export const Clarity: React.FC<ClarityProps> = ({ projectId = 't49kj0f7z1', enabled = true }) => {
    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            return;
        }

        // Wait for Clarity to be available
        const initializeClarity = () => {
            if (window.clarity) {
                // Set up custom events and tracking
                console.log('Microsoft Clarity initialized with project ID:', projectId);

                // Track page views for React Router
                window.clarity('set', 'page', window.location.pathname);

                // Set custom properties
                window.clarity('set', 'app_name', 'WORQ Public Site');
                window.clarity('set', 'app_version', '1.0.0');

                // Track environment
                window.clarity('set', 'environment', process.env.NODE_ENV || 'production');
            } else {
                // Retry if Clarity isn't loaded yet
                setTimeout(initializeClarity, 100);
            }
        };

        // Initialize after a short delay to ensure Clarity script is loaded
        setTimeout(initializeClarity, 500);
    }, [projectId, enabled]);

    // Track route changes for React Router
    useEffect(() => {
        if (!enabled || !window.clarity) {
            return;
        }

        const handleRouteChange = () => {
            window.clarity('set', 'page', window.location.pathname);
        };

        // Listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', handleRouteChange);

        // Also track when the component mounts (for initial page load)
        handleRouteChange();

        return () => {
            window.removeEventListener('popstate', handleRouteChange);
        };
    }, [enabled]);

    // This component doesn't render anything
    return null;
};

/**
 * Utility functions for Clarity tracking
 */
export const ClarityUtils = {
    /**
     * Track custom events
     */
    trackEvent: (eventName: string, data?: unknown) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('event', eventName, data);
        }
    },

    /**
     * Track user actions
     */
    trackAction: (action: string, target?: string) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('event', 'user_action', { action, target });
        }
    },

    /**
     * Track page views manually
     */
    trackPageView: (pageName?: string) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('set', 'page', pageName || window.location.pathname);
        }
    },

    /**
     * Set custom user properties
     */
    setUserProperty: (key: string, value: string) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('set', key, value);
        }
    },

    /**
     * Track form submissions
     */
    trackFormSubmission: (formName: string, success = true) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('event', 'form_submission', {
                form_name: formName,
                success,
            });
        }
    },

    /**
     * Track button clicks
     */
    trackButtonClick: (buttonName: string, location?: string) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('event', 'button_click', {
                button_name: buttonName,
                location,
            });
        }
    },
};

Clarity.propTypes = {
    projectId: PropTypes.string,
    enabled: PropTypes.bool,
};

export default Clarity;
